import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { SITE } from '../../config/site';

/**
 * Per-page SEO: title, description, canonical, Open Graph, Twitter and JSON-LD.
 * <Seo title="Features" description="…" jsonLd={[…]} />
 */
export default function Seo({ title, description = SITE.description, image = SITE.ogImage, noindex = false, jsonLd = [] }) {
    const { pathname } = useLocation();
    const url = `${SITE.siteUrl}${pathname === '/' ? '/' : pathname.replace(/\/$/, '')}`;
    const fullTitle = title ? `${title} | ${SITE.name}` : `${SITE.name}: ${SITE.tagline}`;
    const img = image.startsWith('http') ? image : `${SITE.siteUrl}${image}`;

    return (
        <Helmet prioritizeSeoTags>
            <html lang="en-IN" />
            <title>{fullTitle}</title>
            <meta name="description" content={description} />
            <link rel="canonical" href={url} />
            <meta name="robots" content={noindex ? 'noindex, follow' : 'index, follow, max-image-preview:large'} />

            <meta property="og:type" content="website" />
            <meta property="og:site_name" content={SITE.name} />
            <meta property="og:locale" content="en_IN" />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:url" content={url} />
            <meta property="og:image" content={img} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:image:alt" content={`${SITE.name}: ${SITE.tagline}`} />

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={img} />

            {jsonLd.map((obj, i) => (
                <script key={i} type="application/ld+json">{JSON.stringify(obj)}</script>
            ))}
        </Helmet>
    );
}

/* ---------- reusable structured data ---------- */
export const organizationLd = () => ({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE.name,
    url: SITE.siteUrl,
    logo: `${SITE.siteUrl}/logo512.png`,
    email: SITE.email,
    telephone: SITE.phone,
    address: { '@type': 'PostalAddress', addressLocality: SITE.city.split(',')[0], addressCountry: 'IN' },
    contactPoint: [{ '@type': 'ContactPoint', telephone: SITE.phone, contactType: 'sales', areaServed: 'IN', availableLanguage: ['en', 'hi'] }],
});

export const softwareLd = () => ({
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: SITE.name,
    applicationCategory: 'BusinessApplication',
    applicationSubCategory: 'Salon management software',
    operatingSystem: 'Web, Android, iOS',
    url: SITE.siteUrl,
    description: SITE.description,
    featureList: [
        'GST billing and invoices', 'Prepaid cards and discount memberships', 'Service packages with visit tracking',
        'Appointments with WhatsApp reminders', 'Client profiles and digital khata', 'Staff attendance, incentives and payroll',
        'Sales, staff and GST reports', 'WhatsApp marketing campaigns', 'Multi-branch control',
    ],
    publisher: { '@type': 'Organization', name: SITE.name, url: SITE.siteUrl },
});

export const faqLd = (items) => ({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map(([q, a]) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } })),
});

export const breadcrumbLd = (trail) => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: trail.map(([name, path], i) => ({ '@type': 'ListItem', position: i + 1, name, item: `${SITE.siteUrl}${path}` })),
});
